import {
  AppStatusEnum,
  BlastEmailMessageTypeEnum,
  HospitalFacilityType,
  OrgBlastEmailContext,
  type OrgBlastEmailContextKey,
  PocNotificationRole,
  PTBlastEmailMessageTypeEnum,
  ProgramCodes,
  ProgramTitles,
  RequestStatus,
} from 'common/enum';

const AppListDefault: AppProgram[] = [
  {
    code: ProgramCodes.FILE_LIBRARY,
    title: ProgramTitles.FILE_LIBRARY,
    visible: true,
    active: false,
    hasPermission: true,
    status: AppStatusEnum.GRANT,
  },
  {
    code: ProgramCodes.RESOURCE_REQUEST,
    title: ProgramTitles.RESOURCE_REQUEST,
    visible: true,
    active: false,
    hasPermission: true,
    status: AppStatusEnum.GRANT,
  },
  {
    code: ProgramCodes.PATIENT_TRANSFER,
    title: ProgramTitles.PATIENT_TRANSFER,
    visible: true,
    active: false,
    hasPermission: true,
    status: AppStatusEnum.GRANT,
  },
  {
    code: ProgramCodes.MOD,
    title: ProgramTitles.MOD,
    visible: true,
    active: false,
    hasPermission: true,
    status: AppStatusEnum.GRANT,
  },
  {
    code: ProgramCodes.FAQ,
    title: ProgramTitles.FAQ,
    visible: true,
    active: false,
    hasPermission: true,
    status: AppStatusEnum.GRANT,
  },
  {
    code: ProgramCodes.HOSPITAL,
    title: ProgramTitles.HOSPITAL,
    visible: true,
    active: false,
    hasPermission: true,
    status: AppStatusEnum.GRANT,
  },
];

const UOMOptions: OptionSelect[] = [
  { label: 'Battalion', value: 'battalion' },
  { label: 'Bottle', value: 'bottle' },
  { label: 'Box', value: 'box' },
  { label: 'Case', value: 'case' },
  { label: 'Company', value: 'company' },
  { label: 'Dozen', value: 'dozen' },
  { label: 'Each', value: 'each' },
  { label: 'Gallon', value: 'gallon' },
  { label: 'Half Truckload', value: 'half-truckload' },
  { label: 'Individual', value: 'individual' },
  { label: 'Liter', value: 'liter' },
  { label: 'Pallet', value: 'pallet' },
  { label: 'Platoon', value: 'platoon' },
  { label: 'Pound', value: 'pound' },
  { label: 'Squad', value: 'squad' },
  { label: 'Strike Team', value: 'strike-team' },
  { label: 'Task Force', value: 'task-force' },
  { label: 'Team', value: 'team' },
  { label: 'Ton', value: 'ton' },
  { label: 'Truckload', value: 'truckload' },
];

// The 13 patient-transfer bed types, alphabetical case-insensitively so a coordinator
// picking one under time pressure can scan the list. `label === value` on every entry:
// one string is the dropdown row, the stored value, and the CSV cell.
//
// Byte-for-byte match with dph-portal-api's PATIENT_BED_TYPES (src/constants/index.ts) --
// keep both in sync by hand. That constant is the enum on CreatePatientTransferSchema and
// CreateBulkPatientTransferSchema, both serialized into AWS::ApiGateway::Model resources,
// so a one-character difference is a gateway 400 ahead of any handler and the response
// names no field. Adding a bed type here means adding it there first and deploying that
// side before this one.
//
// The three BHU values carry an en dash (U+2013), not a hyphen -- a hyphen is a different
// string and a different enum member. `BedType` in src/interfaces/types.d.ts restates these
// same 13 and is hand-synced too; that file is an ambient global.
const BedTypeOptions: OptionSelect[] = [
  { label: 'BHU – adolescence', value: 'BHU – adolescence' },
  { label: 'BHU – adult', value: 'BHU – adult' },
  { label: 'BHU – geriatric', value: 'BHU – geriatric' },
  {
    label: 'CICU (cardiac intensive care unit)',
    value: 'CICU (cardiac intensive care unit)',
  },
  { label: 'ICU', value: 'ICU' },
  { label: 'L&D / OBGYN', value: 'L&D / OBGYN' },
  { label: 'LTAC', value: 'LTAC' },
  { label: 'Med-Surg', value: 'Med-Surg' },
  { label: 'Med-Tele', value: 'Med-Tele' },
  { label: 'NICU', value: 'NICU' },
  { label: 'Normal Nursery', value: 'Normal Nursery' },
  { label: 'PCU', value: 'PCU' },
  { label: 'Peds', value: 'Peds' },
];

// The five hospital facility types, derived from the enum rather than restated so the set the
// org form validates and the set its dropdown offers cannot drift apart inside this repo.
// `label === value` on every entry, matching the API's own constant -- one string is the
// dropdown row and the stored value. Already alphabetical there, and `Object.values` preserves
// declaration order, so the list renders alphabetically without a sort.
//
// The cross-repo hazard lives on the enum itself (src/common/enum/index.ts); read the comment
// above `HospitalFacilityType` before changing a value.
const HospitalFacilityTypeOptions: OptionSelect[] = Object.values(HospitalFacilityType).map(
  (value) => ({ label: value, value })
);

const EMSOptions: OptionSelect[] = [
  { label: 'ALS', value: 'ALS' },
  { label: 'BLS', value: 'BLS' },
  { label: 'CCT', value: 'CCT' },
  { label: 'Hospital Arranged', value: 'hospital-arranged' },
];

const ResReqStatusOptions: OptionSelect[] = Object.entries(RequestStatus).map((req) => ({
  label: req[1],
  value: req[1],
}));

// Sentence case, rendered uppercase by the chip styling in the contact modal's Type column --
// the dropdown list itself reads better in sentence case. All three are real values and all
// three render identically; the Figma styles `Notify` like a placeholder, which would read as
// "nothing selected".
const PocNotificationRoleOptions: OptionSelect[] = [
  { label: 'Notify', value: PocNotificationRole.NOTIFY },
  { label: 'Primary', value: PocNotificationRole.PRIMARY },
  { label: 'Secondary', value: PocNotificationRole.SECONDARY },
];

/*
 * The four notification events, each pairing the role column that carries the designation
 * with the boolean the API still requires on the wire.
 *
 * Both field names are pinned to `OrganizationPOCData` through `Extract`, so renaming a field
 * in `src/interfaces/interface.d.ts` collapses the corresponding member to `never` and fails
 * the build here. Without that, a rename would leave these string literals pointing at
 * columns that no longer exist and every role would silently stop saving -- the exact failure
 * mode this table exists to make visible.
 */
type PocNotificationFlagField = Extract<
  keyof OrganizationPOCData,
  'resourceRequest' | 'patientTransfer' | 'inventory' | 'bedCount'
>;

type PocNotificationRoleField = Extract<
  keyof OrganizationPOCData,
  `${PocNotificationFlagField}Role`
>;

type PocNotificationEvent = {
  label: string;
  flagField: PocNotificationFlagField;
  roleField: PocNotificationRoleField;
};

const PocNotificationEvents: readonly PocNotificationEvent[] = [
  { label: 'Resource request', flagField: 'resourceRequest', roleField: 'resourceRequestRole' },
  { label: 'Patient transfer', flagField: 'patientTransfer', roleField: 'patientTransferRole' },
  { label: 'Inventory', flagField: 'inventory', roleField: 'inventoryRole' },
  // "Bed count updates", not "Bed count": the design renames this row, and it is the only one
  // of the four whose label differs from its field name.
  { label: 'Bed count updates', flagField: 'bedCount', roleField: 'bedCountRole' },
];

const PalmettoResourceRequestStaticInfo = {
  pvIncidentID: 2893,
  pvIncidentName: '2025 Healthcare Resource Requests',
  pvUserID: 13577,
  pvPositionID: 779,
};

const BlastEmailMessageTypeOptions: OptionSelect[] = [
  { label: 'Message', value: 'message' },
  { label: 'Contacts', value: 'contacts' },
];

const BedCountBlastEmailMessageTypeOptions: OptionSelect[] = [
  { label: 'Message', value: 'message' },
  { label: 'Bed Counts', value: 'bed_counts' },
];

const PTBlastEmailMessageTypeOptions: OptionSelect[] = [
  { label: 'Hurricane Evacuation', value: 'hurricane' },
  { label: 'Local/Regional Event', value: 'local_regional' },
];

// Client-side mirror of the bounds dph-portal-api enforces on blast email fields
// (SendPTBlastSchema in src/schemas/patient.schemas.ts, BlastEmailSchema in
// src/schemas/organization.schemas.ts). The API validates at its own trust
// boundary; these are a usability guard so an oversized body fails in the compose
// box rather than after the patient request has already been created -- at that
// point the send is a warning toast, the author is navigated away, and the body
// they wrote is gone. The two API constants are declared per-file over there, so
// they can drift from each other; re-check both when touching either value here.
const MAX_EMAIL_BODY_LENGTH = 32 * 1024;
const MAX_EMAIL_SUBJECT_LENGTH = 512;

// Visible field labels for the two bounded blast-email fields. Single-sourced
// because each label is read twice -- once by the compose box's <Input label>,
// once by emailLengthErrorMessage below, which quotes the field back to the
// author. Renaming a label in only one place would leave the error message
// naming a field that is no longer on screen.
const EMAIL_SUBJECT_LABEL = 'Email subject';
const EMAIL_BODY_LABEL = 'Message body';

// "Message body is 41,203 characters. The limit is 32,768 -- remove 8,435."
// Naming the overage is what makes the error actionable: an author who pasted a
// long body needs to know how much to cut, not only that it is too long.
// Callers only build this message once the length already exceeds the limit, so
// the subtraction is always positive and an empty field never reaches it.
const emailLengthErrorMessage = (fieldLabel: string, length: number, limit: number): string =>
  `${fieldLabel} is ${length.toLocaleString('en-US')} characters. The limit is ` +
  `${limit.toLocaleString('en-US')} -- remove ${(length - limit).toLocaleString('en-US')}.`;

// The literal phrases dph-portal-api's sendPTBlast scans for to place each
// per-facility magic link (its own PT_BLAST_LINK_TRIGGERS, notifications.controller.ts).
// This is a cross-repo string contract with no compiler, type, or test behind it:
// each phrase must appear exactly once per template body, alone on its own line,
// and nowhere in the surrounding prose. Reword, recase, or re-punctuate one and
// that blast ships with a dead call to action, a success toast, and no error on
// either side of the wire -- the API's warning fires only when BOTH phrases are
// missing. The bodies below interpolate these values so a copy edit cannot drift
// the phrase text away from the contract.
const PT_BLAST_LINK_TRIGGERS = {
  evacuating: 'Patient Transfer Link',
  receiving: 'Receiving Hospital Link',
} as const;

// The author's incident name is spliced into the body inside !!...!!, two
// characters from the start, at template-select time. That splice happens after
// the exactly-once and alone-on-its-own-line properties were verified against
// the template, so an incident name carrying a trigger phrase or its own !!
// pair breaks the contract on the string that actually ships -- and breaks it
// silently, because the API warns only when BOTH phrases are missing.
// Returns null when the name is safe to splice.
const incidentNameContractError = (incidentName: string): string | null => {
  if (incidentName.includes('!!')) {
    return (
      'Incident name cannot contain "!!". The portal wraps the incident name in ' +
      '!! !! to build the email banner, so an extra pair cuts the banner short.'
    );
  }
  const phrase = Object.values(PT_BLAST_LINK_TRIGGERS).find((p) => incidentName.includes(p));
  if (phrase) {
    return (
      `Incident name cannot contain "${phrase}". That exact phrase is what the ` +
      'portal replaces with a secure link, and a second copy breaks both links.'
    );
  }
  return null;
};

/*
  Subject/body per patient-transfer message type. What the body carries, and what
  each repo does with it.

  PENDING -- the renderEmail() pipeline below ships on dph-portal-api's unmerged
  epic-03-unified-email-template branch; src/libs/renderEmail.ts does not exist on
  that repo's main. Until it deploys, main composes these blasts through the older
  patientRequestTemplate: no green drill header, no link plates, no [ name ]
  substitution, and the sign-off and footer come from the body rather than the
  shell. See the pre-merge gate in
  .specs/story-align-blast-email-contract.md > ## Verification.

  The [ name ] substitution now ships on that same branch (sendPTBlast, in
  notifications.controller.ts, mirroring sendOrgBlastEmail) and is covered by
  src/libs/__tests__/pt-blast-name.test.ts there. Verified live on 2026-07-25:
  the greeting renders the recipient facility's name in bold.

  Do not add a blank line after the !!{INCIDENT NAME}!! line to space it off the
  greeting. extractGreenTitle() lifts that whole line into the green header, so
  any blank line left under it renders as a gap between the header and the
  greeting -- the defect fixed in renderEmail.ts on 2026-07-25. Spacing between
  the header and the body belongs to the card shell, not to this copy.

    template body (author-editable in the compose box)
         │
         │   !!{INCIDENT NAME}!!                 ── substituted here, at template-select time
         │   [ name ]                            ── dph-portal-api substitutes the facility name
         │   PT_BLAST_LINK_TRIGGERS.evacuating  ─┐ dph-portal-api replaces IN PLACE with
         │   PT_BLAST_LINK_TRIGGERS.receiving   ─┘ [phrase](per-facility magic URL)
         ▼
    renderEmail(body, { email_title: emailSubject })          [dph-portal-api]
         │
         ├── first !!..!!   → green drill header, stripped from the body
         ├── [label](url)   → block-level blue link plate
         └── \n\n → <br><br>,  \n → <br>
         ▼
    one card shell (sign-off and footer baked in)  →  SES

  Why "alone on its own line" is load-bearing: the link plate is a block-level
  <table>, so a trigger phrase left inside a sentence splits that sentence into
  three stacked blocks.

  The `[ name ]` bracket spacing is load-bearing for the same reason it is in
  OrgBlastEmailTemplates below: the API's substitution regex matches that exact
  spacing. Authors never write [label](url) markers themselves -- the URL is
  per-facility and carries a JWT the compose box does not have.
*/
const PTBlastEmailTemplates: Record<
  (typeof PTBlastEmailMessageTypeEnum)[keyof typeof PTBlastEmailMessageTypeEnum],
  { subject: string; body: string }
> = {
  hurricane: {
    subject: 'Palmetto Healthcare Portal patient request',
    body: `!!{INCIDENT NAME}!!\nGreetings [ name ] representative,\n\nIn preparation for the impending hurricane, the Governor has issued a mandatory medical evacuation. This has triggered activation of the Palmetto Healthcare Portal for any patient movement requests.\n\nIf you are a transferring hospital who may need assistance with patient movement, use the transfer link below:\n\nTransferring Hospitals Click Here:\n${PT_BLAST_LINK_TRIGGERS.evacuating}\n\nIf you are a receiving hospital who can accept patients, use the receiving link below to review and accept patients.\n\nReceiving Hospitals Click Here:\n${PT_BLAST_LINK_TRIGGERS.receiving}`,
  },
  local_regional: {
    subject: 'Healthcare Portal patient request',
    body: `!!{INCIDENT NAME}!!\nGreetings [ name ] representative,\n\nA local or regional event has occurred impacting one or more hospitals. This has triggered activation of the Healthcare Portal for any patient movement requests.\n\nIf you are an impacted hospital who may need assistance with patient movement, use the transfer link below.\n\nTransferring Hospital(s) Click Here:\n${PT_BLAST_LINK_TRIGGERS.evacuating}\n\nIf you are a receiving hospital who can accept patients, use the receiving link below to review and accept patients:\n\nReceiving Hospital(s) Click Here:\n${PT_BLAST_LINK_TRIGGERS.receiving}`,
  },
};

// Requirement 6's single-source constant stops the phrase TEXT from drifting.
// It cannot protect the two properties that actually carry the contract: each
// phrase appearing exactly once, and sitting alone on its own line. Both belong
// to the prose around the phrase, which stays freely editable, so a copy edit
// can break them without touching PT_BLAST_LINK_TRIGGERS at all. This project
// has no test runner (CLAUDE.md), so the assertion runs at module load and
// throws in development -- a broken template fails on the developer's machine
// instead of shipping a blast with a dead call to action and a success toast.
if (import.meta.env.DEV) {
  for (const [messageType, template] of Object.entries(PTBlastEmailTemplates)) {
    for (const phrase of Object.values(PT_BLAST_LINK_TRIGGERS)) {
      const occurrences = template.body.split(phrase).length - 1;
      if (occurrences !== 1) {
        throw new Error(
          `PTBlastEmailTemplates.${messageType}: "${phrase}" appears ${occurrences} times. ` +
            'dph-portal-api replaces it in place and expects exactly one occurrence.'
        );
      }
      const line = template.body.split('\n').find((l) => l.includes(phrase));
      if (line?.trim() !== phrase) {
        throw new Error(
          `PTBlastEmailTemplates.${messageType}: "${phrase}" must sit alone on its own line, ` +
            `but it is inside "${line?.trim()}". The link plate is a block-level <table>, so an ` +
            'in-prose phrase splits that sentence into three stacked blocks.'
        );
      }
    }
    if (template.body.includes('](')) {
      throw new Error(
        `PTBlastEmailTemplates.${messageType}: body contains a [label](url) marker. Authors ` +
          'never write these -- the magic URL is per-facility and carries a JWT the compose ' +
          'box does not hold.'
      );
    }
  }
}

// Subject/body per Organizations "Blast email" message type. The `[ name ]` and
// `[ link ]` placeholder spacing is load-bearing: dph-portal-api's substitution
// regex matches that exact spacing, so a mismatch silently ships literal
// placeholder text to real inboxes. Keep the spaces.
// Keyed off BlastEmailMessageTypeEnum (minus MESSAGE, which has no template and
// clears to editable fields) so a new templated message type forces a matching
// entry here, mirroring PTBlastEmailTemplates. Prevents silent enum/template drift.
const OrgBlastEmailTemplates: Record<
  Exclude<
    (typeof BlastEmailMessageTypeEnum)[keyof typeof BlastEmailMessageTypeEnum],
    typeof BlastEmailMessageTypeEnum.MESSAGE
  >,
  { subject: string; body: string }
> = {
  contacts: {
    subject: 'Organization Contacts Update Requested',
    body: `Please proceed to the following address to update or confirm the contact listing for [ name ]. \n\n[ link ]\n\n`,
  },
  bed_counts: {
    subject: 'Hospital Bed Count update requested',
    body: `Greetings,\n\nAction Required: Please review and submit your facility's current bed counts for [ name ].\n\nAs the designated bed-count contact for this facility, you are requested to submit your facility's current bed counts. Maintaining accurate, up-to-date bed capacity is essential for regional emergency coordination.\n\nPlease click the link below to submit your facility's bed counts:\n[ link ]\n\nYour prompt attention to this request is appreciated.\n\nRespectfully,\n\nThe SCHCC Team`,
  },
};

// The bed-count action gives the free-form "message" template a starting default
// instead of clearing to blank, so a hospital bed-count blast keeps the [ link ]
// placeholder even when the sender switches away from the Bed Counts template.
// Subject mirrors the bed_counts template; keep the [ link ] spacing (see above).
const BedCountMessageTemplate: { subject: string; body: string } = {
  subject: 'Hospital Bed Count update requested',
  body: `Please click the link below to submit your facility's bed counts:\n[ link ]`,
};

// Shared template lookup for the Organizations blast-email flow. Both the user
// picking a template (OrgBlastContent) and the bed-count context preselecting one
// (OrgBlastEmailModal.container) apply subject/body through here. Message types
// with no template ("message") return empty strings, clearing to editable fields --
// except in the bed-count context, which prefills "message" with a link default.
const applyOrgBlastEmailTemplate = (
  messageType: string,
  context?: OrgBlastEmailContextKey
): { subject: string; body: string } => {
  if (
    context === OrgBlastEmailContext.BED_COUNT &&
    messageType === BlastEmailMessageTypeEnum.MESSAGE
  ) {
    return BedCountMessageTemplate;
  }
  return (
    OrgBlastEmailTemplates[messageType as keyof typeof OrgBlastEmailTemplates] ?? {
      subject: '',
      body: '',
    }
  );
};

export {
  AppListDefault,
  applyOrgBlastEmailTemplate,
  BedCountBlastEmailMessageTypeOptions,
  BedTypeOptions,
  BlastEmailMessageTypeOptions,
  EMAIL_BODY_LABEL,
  emailLengthErrorMessage,
  EMAIL_SUBJECT_LABEL,
  EMSOptions,
  HospitalFacilityTypeOptions,
  incidentNameContractError,
  MAX_EMAIL_BODY_LENGTH,
  MAX_EMAIL_SUBJECT_LENGTH,
  OrgBlastEmailTemplates,
  PalmettoResourceRequestStaticInfo,
  PocNotificationEvents,
  type PocNotificationEvent,
  type PocNotificationFlagField,
  type PocNotificationRoleField,
  PocNotificationRoleOptions,
  PT_BLAST_LINK_TRIGGERS,
  PTBlastEmailMessageTypeOptions,
  PTBlastEmailTemplates,
  ResReqStatusOptions,
  UOMOptions,
};
